import React from 'react'
import type { BlockAnimation, Variable } from '../types'

interface AnimationEditorProps {
  animation?: BlockAnimation
  variables: Variable[]
  onChange: (animation: BlockAnimation | undefined) => void
}

const EFFECTS: { value: BlockAnimation['effect']; label: string }[] = [
  { value: 'fade', label: 'Fade In' },
  { value: 'slide-up', label: 'Slide Up' },
  { value: 'slide-down', label: 'Slide Down' },
  { value: 'slide-left', label: 'Slide Left' },
  { value: 'slide-right', label: 'Slide Right' },
  { value: 'vibrate', label: 'Vibrate' },
  { value: 'typing', label: 'Typing' },
]

const TRIGGERS: { value: BlockAnimation['trigger']; label: string }[] = [
  { value: 'on-appear', label: 'On Appear' },
  { value: 'on-variable-change', label: 'On Variable Change' },
]

function PropertyLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">
      {children}
    </label>
  )
}

export default function AnimationEditor({ animation, variables, onChange }: AnimationEditorProps) {
  const isEnabled = !!animation

  const handleToggle = () => {
    if (isEnabled) {
      onChange(undefined)
    } else {
      onChange({ effect: 'fade', duration: 400, delay: 0, trigger: 'on-appear' })
    }
  }

  const update = (updates: Partial<BlockAnimation>) => {
    if (!animation) return
    onChange({ ...animation, ...updates })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <PropertyLabel>Animation</PropertyLabel>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={isEnabled}
            onChange={handleToggle}
          />
          <div className="w-10 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#5048e5]"></div>
        </label>
      </div>

      {isEnabled && (
        <>
          <div className="space-y-2">
            <PropertyLabel>Effect</PropertyLabel>
            <select
              value={animation.effect}
              onChange={(e) => update({ effect: e.target.value as BlockAnimation['effect'] })}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#5048e5] focus:border-transparent outline-none"
            >
              {EFFECTS.map((ef) => (
                <option key={ef.value} value={ef.value}>{ef.label}</option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <PropertyLabel>Trigger</PropertyLabel>
            <select
              value={animation.trigger}
              onChange={(e) => {
                const trigger = e.target.value as BlockAnimation['trigger']
                update({
                  trigger,
                  watchVariable: trigger === 'on-variable-change' ? (variables[0]?.name || undefined) : undefined,
                })
              }}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#5048e5] focus:border-transparent outline-none"
            >
              {TRIGGERS.map((tr) => (
                <option key={tr.value} value={tr.value}>{tr.label}</option>
              ))}
            </select>
          </div>

          {/* Variable picker when trigger is on-variable-change */}
          {animation.trigger === 'on-variable-change' && (
            <div className="space-y-2">
              <PropertyLabel>Watch Variable</PropertyLabel>
              {variables.length > 0 ? (
                <select
                  value={animation.watchVariable || ''}
                  onChange={(e) => update({ watchVariable: e.target.value || undefined })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm font-mono focus:ring-2 focus:ring-[#5048e5] focus:border-transparent outline-none"
                >
                  {variables.map((v) => (
                    <option key={v.name} value={v.name}>@{v.name}</option>
                  ))}
                </select>
              ) : (
                <p className="text-xs text-slate-400 italic">No variables yet — add a Slider or create one from a Button/Chart.</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <PropertyLabel>Duration (ms)</PropertyLabel>
              <input
                type="number"
                value={animation.duration}
                onChange={(e) => update({ duration: Math.max(100, Number(e.target.value)) })}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#5048e5] focus:border-transparent outline-none"
                min={100}
                step={100}
              />
            </div>
            <div className="space-y-2">
              <PropertyLabel>Delay (ms)</PropertyLabel>
              <input
                type="number"
                value={animation.delay}
                onChange={(e) => update({ delay: Math.max(0, Number(e.target.value)) })}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#5048e5] focus:border-transparent outline-none"
                min={0}
                step={100}
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
